import { type DataPoint, Dates } from 'venn-utils';
import type { FrequencyEnum } from 'venn-api';

/**
 * Separates a list of data points into proxy and extrapolated groups based on specified date ranges.
 *
 * @param proxiedInvestment - The array of data points to process.
 * @param proxyStartDate - The start timestamp for the proxy range (inclusive).
 * @param proxyEndDate - The end timestamp for the proxy range (inclusive).
 * @param extrapolateStartDate - The start timestamp for the extrapolation range (inclusive).
 *                               Points falling in this range take precedence over the proxy range.
 * @returns An object containing two arrays: `proxy` and `extrapolated`.
 */

export const extractProxiedReturns = (
  proxiedInvestment: DataPoint[],
  proxyStartDate: number | undefined,
  proxyEndDate: number | undefined,
  extrapolateStartDate: number | undefined,
) => {
  return proxiedInvestment.reduce<{ proxy: DataPoint[]; extrapolated: DataPoint[] }>(
    (acc, point) => {
      if (extrapolateStartDate && point.x >= extrapolateStartDate) {
        acc.extrapolated.push(point);
      } else if (proxyStartDate && proxyEndDate && proxyStartDate <= point.x && point.x <= proxyEndDate) {
        acc.proxy.push(point);
      }
      return acc;
    },
    { proxy: [], extrapolated: [] },
  );
};

/**
 * Formats a start and end timestamp into a date range string based on the given frequency.
 *
 * @param start - The start timestamp.
 * @param end - The end timestamp.
 * @param frequency - The frequency enum to determine the date format.
 * @returns A formatted string representing the date range (e.g., "DD MMM YYYY - DD MMM YYYY").
 */
export const formatDateRange = (start: number, end: number, frequency: FrequencyEnum) => {
  return `${Dates.toDDMMMYYYY(start, frequency)} - ${Dates.toDDMMMYYYY(end, frequency)}`;
};
